import axiosClient from "./axios";
import { ADMIN_LOGIN_URL, ADMIN_GMAIL_LOGIN_URL } from "../constants/endpoints";
import {
  getAuth,
  signInWithCustomToken,
  onAuthStateChanged,
  signInWithPopup,
  GoogleAuthProvider,
  setPersistence,
  browserLocalPersistence,
  Unsubscribe,
} from "@firebase/auth";
import { auth } from "../firebase";
import { logout } from "../utils/logout";
import { getSession, setSession, deleteSession } from "../utils/session";
import { resetAuthState } from "../utils/firebaseAuth";
import { isBlockedFromApp } from "../utils/roles";
import * as Sentry from "@sentry/react";

// Shown to users whose role is not permitted to access this admin web app.
export const ACCESS_DENIED_ERROR_MESSAGE =
  "This account isn't authorized to access the admin dashboard.";

// Flag to track if we're in the middle of an intentional sign-out before new login
let isIntentionalSignOut = false;

// Flag to track if firebaseLogin is currently in progress (mutex)
let firebaseLoginInProgress = false;

// Store the auth state listener unsubscribe function
let authStateUnsubscribe: Unsubscribe | null = null;

class LoginService {
  /**
   * Check if firebaseLogin is currently in progress
   */
  static isFirebaseLoginInProgress(): boolean {
    return firebaseLoginInProgress;
  }

  /**
   * Clear the custom token from session - call this on any auth failure
   */
  static clearCustomToken(): void {
    deleteSession("customToken");
  }

  /**
   * Extract and store role from Firebase token claims
   */
  private static async extractAndStoreRole(): Promise<string> {
    const user = getAuth().currentUser;
    if (!user) {
      return "";
    }

    try {
      const idTokenResult = await user.getIdTokenResult();
      let role: string = "";
      
      if (
        idTokenResult.claims &&
        typeof idTokenResult.claims === "object" &&
        "roles" in idTokenResult.claims &&
        idTokenResult.claims.roles &&
        typeof idTokenResult.claims.roles === "object" &&
        "data" in idTokenResult.claims.roles &&
        typeof idTokenResult.claims.roles.data === "string"
      ) {
        role = idTokenResult.claims.roles.data;
      }

      if (role && role.trim() !== "") {
        setSession("role", role);
        setSession("userId", user.uid || "");
      }

      return role;
    } catch (error) {
      Sentry.captureException(error, {
        level: "error",
        tags: { auth_flow: "extract_role" },
      });
      return "";
    }
  }

  static async customToken(email: string, password: string) {
    // Set flag to prevent auth listener from triggering logout during intentional sign-out
    isIntentionalSignOut = true;
    
    // Reset auth state for fresh login
    resetAuthState();
    
    // Always sign out the current Firebase user before logging in as a new one
    try {
      await getAuth().signOut();
    } catch (e) {
      // ignore errors, e.g. if not signed in
    } finally {
      // Reset flag after sign-out completes
      isIntentionalSignOut = false;
    }

    // Ensure persistence is set to LOCAL before login
    const authInstance = getAuth();
    await setPersistence(authInstance, browserLocalPersistence);

    let response;
    try {
      response = await axiosClient.get(ADMIN_LOGIN_URL, {
        headers: {
          email: email,
          "firebase-user-password": password,
        },
      });
    } catch (error) {
      // Clear any stale custom token on login failure
      LoginService.clearCustomToken();
      throw error;
    }

    const customToken = response.data.result.data.auth_token;
    
    if (!customToken) {
      const error = new Error("Backend did not return a custom token");
      Sentry.captureException(error, {
        level: "error",
        tags: { auth_flow: "custom_token" },
      });
      // Clear any stale token
      LoginService.clearCustomToken();
      throw error;
    }
    
    setSession("customToken", customToken);
    return response;
  }

  static async googleLogin() {
    // Set flag to prevent auth listener from triggering logout during intentional sign-out
    isIntentionalSignOut = true;
    
    // Reset auth state for fresh login
    resetAuthState();
    
    // Always sign out the current Firebase user before logging in as a new one
    try {
      await getAuth().signOut();
    } catch (e) {
      // ignore errors, e.g. if not signed in
    } finally {
      // Reset flag after sign-out completes
      isIntentionalSignOut = false;
    }

    // Ensure persistence is set to LOCAL before login
    const authInstance = getAuth();
    await setPersistence(authInstance, browserLocalPersistence);

    const provider = new GoogleAuthProvider();
    let result;
    try {
      result = await signInWithPopup(auth, provider);
    } catch (error: any) {
      // Clear any stale custom token on popup failure/cancel
      LoginService.clearCustomToken();
      throw error;
    }
    
    const user = result.user;

    // Get the ID token from the signed-in user
    const idToken = await user.getIdToken();

    let response;
    try {
      response = await axiosClient.get(ADMIN_GMAIL_LOGIN_URL, {
        headers: {
          "firebase-user-id": user.uid,
          "x-auth-token": idToken,
        },
      });
    } catch (error) {
      // Clear any stale custom token on backend failure
      LoginService.clearCustomToken();
      throw error;
    }

    const customToken = response.data.result.data.auth_token;
    
    if (!customToken) {
      const error = new Error("Backend did not return a custom token for Google login");
      Sentry.captureException(error, {
        level: "error",
        tags: { auth_flow: "google_login" },
      });
      // Clear any stale token
      LoginService.clearCustomToken();
      throw error;
    }
    
    setSession("customToken", customToken);
    return response;
  }

  static async firebaseToken() {
    // Mutex: prevent concurrent firebaseLogin calls
    if (firebaseLoginInProgress) {
      Sentry.captureMessage("firebaseToken called while already in progress - skipping", {
        level: "warning",
        tags: { auth_flow: "firebase_token_mutex" },
      });
      throw new Error("Firebase login already in progress");
    }
    
    firebaseLoginInProgress = true;
    
    try {
      const customToken = getSession("customToken");
      if (!customToken) {
        const error = new Error("No custom token found in session during firebaseToken call");
        Sentry.captureException(error, {
          level: "warning",
          tags: { auth_flow: "firebase_token" },
        });
        // Don't call logout() here - just clear token and throw
        // This prevents redirect loops
        LoginService.clearCustomToken();
        throw error;
      }

      // Sign in with Firebase using the custom token if not already authenticated
      const authInstance = getAuth();
      let shouldSignIn = !authInstance.currentUser;

      // Also check if current user has no role claims
      if (authInstance.currentUser && !shouldSignIn) {
        try {
          const idTokenResult = await authInstance.currentUser.getIdTokenResult();
          const hasRole = idTokenResult.claims &&
            typeof idTokenResult.claims === "object" &&
            "roles" in idTokenResult.claims &&
            idTokenResult.claims.roles &&
            typeof idTokenResult.claims.roles === "object" &&
            "data" in idTokenResult.claims.roles &&
            typeof idTokenResult.claims.roles.data === "string" &&
            idTokenResult.claims.roles.data.length > 0;

          if (!hasRole) {
            shouldSignIn = true;
          }
        } catch (error) {
          Sentry.captureException(error);
          shouldSignIn = true;
        }
      }

      if (shouldSignIn) {
        try {
          await signInWithCustomToken(auth, customToken);
        } catch (signInError: any) {
          // Clear the invalid custom token to prevent loops
          LoginService.clearCustomToken();
          throw signInError;
        }
      }

      // Attach token refresh listener after successful sign-in
      LoginService.attachTokenRefreshListener();

      const user = getAuth().currentUser;
      if (!user) {
        const error = new Error("No authenticated user after sign-in");
        Sentry.captureException(error, {
          level: "error",
          tags: { auth_flow: "firebase_token" },
        });
        // Clear token to prevent loops
        LoginService.clearCustomToken();
        throw error;
      }

      // Extract and store role from token claims
      const role = await LoginService.extractAndStoreRole();
      
      // If role is empty, this is a critical error - clear tokens and sign out to prevent loops
      if (!role || role.trim() === "") {
        const error = new Error("User role not found in Firebase token claims");
        Sentry.captureException(error, {
          level: "error",
          tags: { auth_flow: "firebase_token" },
          extra: { userId: user.uid },
        });
        // CRITICAL: Sign out from Firebase to prevent auth state from persisting
        // This prevents the loop where onAuthStateChanged sees a user but no role exists
        try {
          await getAuth().signOut();
        } catch (signOutError) {
          Sentry.captureException(signOutError, {
            level: "warning",
            tags: { auth_flow: "role_missing_signout" },
          });
        }
        // Clear tokens to prevent loops - user needs to contact admin
        LoginService.clearCustomToken();
        throw error;
      }

      // Deny login for roles that belong to other applications (e.g.
      // SHOP_CATALOG_EDITOR, SHOP_DELIVERY). These accounts can be created from
      // this admin app's Employee Management, but they are meant for a
      // different app and must not be allowed to establish a session here.
      if (isBlockedFromApp(role)) {
        const error = new Error(ACCESS_DENIED_ERROR_MESSAGE);
        error.name = "AccessDeniedError";
        Sentry.captureMessage("Login blocked for role not permitted in admin app", {
          level: "warning",
          tags: { auth_flow: "role_not_allowed" },
          extra: { userId: user.uid, role },
        });
        // Sign out of Firebase so onAuthStateChanged does not see a lingering
        // authenticated user, then clear the custom token to prevent loops.
        // Flag the sign-out as intentional so the token-refresh listener
        // (attached above) does not fire its own logout() hard-redirect, which
        // would reload the page and swallow the inline access-denied message.
        isIntentionalSignOut = true;
        try {
          await getAuth().signOut();
        } catch (signOutError) {
          Sentry.captureException(signOutError, {
            level: "warning",
            tags: { auth_flow: "role_not_allowed_signout" },
          });
        } finally {
          isIntentionalSignOut = false;
        }
        LoginService.clearCustomToken();
        throw error;
      }

      return { data: user };
    } catch (error: any) {
      Sentry.captureException(error);
      if (error.code === "auth/invalid-custom-token") {
        Sentry.captureMessage("Invalid or expired custom token", {
          level: "error",
          tags: { auth_flow: "invalid_custom_token" },
        });
        // Clear the invalid token to prevent loops
        LoginService.clearCustomToken();
      }
      throw error;
    } finally {
      // Always release the mutex
      firebaseLoginInProgress = false;
    }
  }

  static async refreshToken() {
    const user = getAuth().currentUser;
    if (!user) {
      Sentry.captureMessage("No authenticated user during token refresh", {
        level: "error",
        tags: { auth_flow: "token_refresh" },
      });
      logout();
      return null;
    }

    try {
      // Firebase SDK handles token refresh automatically
      // Force refresh to ensure we get a new token
      const idToken = await user.getIdToken(true);
      return idToken;
    } catch (error) {
      Sentry.captureException(error);
      logout();
      return null;
    }
  }

  static attachTokenRefreshListener() {
    // Unsubscribe from previous listener if exists to prevent multiple listeners
    if (authStateUnsubscribe) {
      authStateUnsubscribe();
      authStateUnsubscribe = null;
    }
    
    const authInstance = getAuth();

    // Track if this listener should handle logout
    // Only logout if we had a user and then lost them (not on initial null state)
    let hadUser = !!authInstance.currentUser;

    authStateUnsubscribe = onAuthStateChanged(authInstance, async (user) => {
      if (user) {
        hadUser = true;
        try {
          // Extract and store role from token claims
          await LoginService.extractAndStoreRole();
        } catch (error) {
          Sentry.captureException(error);
        }
      } else if (hadUser && !isIntentionalSignOut) {
        // Only logout if:
        // 1. We previously had a user (session expired/signed out)
        // 2. This is NOT an intentional sign-out before new login
        logout();
      }
    });
  }
  
  /**
   * Reset the firebaseLogin mutex - call this when cleaning up auth state
   */
  static resetFirebaseLoginMutex(): void {
    firebaseLoginInProgress = false;
  }
}

export default LoginService;
